import React from 'react'
import LoadingBanner from '../../components/LoadingBanner'
import ProjectBiddingWizardStep from './BiddingWizardStep'
import BiddingTalentStep from './BiddingTalentStep'
import BiddingBriefStep from "./BiddingBriefStep";
import {useProject} from "../../providers/ClientProjectProvider";
import ShareModal from "./components/ShareModal";
import {useLocation, useNavigate, useOutletContext} from "react-router-dom";
import {ProjectPositionProvider} from "../../providers/ProjectPositionProvider";
import PositionShortlistedCandidates from "./PositionShortlistedCandidates";
import {useIsMutating} from '@tanstack/react-query';
import {Affix} from "antd";
import {useAuth} from "../../providers/AuthProvider";
import UserImageCircle from '../../components/UserImageCircle';
import LoadingSpinner from "../../components/LoadingSpinner";
import InfoTooltip from "../../components/InfoTooltip";
import ClientLeadManagementTools from "../client-lead-tools/ClientLeadManagementTools";
import {TeamProposalsWizardStep} from "./BiddingProposalsStep";

export default class BiddingCandidatesStep extends ProjectBiddingWizardStep {
    static slug = 'shortlist';
    static path = '/shortlist';
    static StepName = 'Shortlist';
    // static StepName = 'Select Hires';
    static StepTitle = 'Shortlist';
    static StepHeader = 'Interview and finalize open positions from invited candidates';

    static stepNameFor(project) {
        return this.StepName;
    }

    static stepBadgeFor(project) {
        return TB.sumBy(project.positions || [], 'total_selected_candidates') || undefined;
    }

    static iconFor() {
        return <img src={TB.logoImagePath()} alt="" className="tab-icon"/>
    }

    static accomplishedForProject(project = {}) {
        return project.shortlisted_candidates_count;
    }

    constructor(props) {
        super(props);
        let isLoading = this.props.project.is_on_hold;

        if (props.project.positions?.length) {
            let total = TB.sumBy(props.project.positions, 'total_selected_candidates')
            if (total <= 0) {
                isLoading = true;
            }
        }

        this.state = {
            ...this.state,
            isLoading,
            potentialTalent: this.props.project.isBYO() ? TB.tryArray(this.props.project.sortedPositions()) : null,
        };
    }

    async componentDidMount() {
        super.componentDidMount();

        await this.middlewareCheck(async () => {

            await TB.queryClient.removeQueries({predicate: ({queryKey}) => queryKey[0] === 'searchCandidates'})
            this.setState({isLoading: false})

        });
    }

    async middlewareCheck(callback) {
        if (!this.context?.auth?.canViewTalentForProject(this.props.project)) {
            await TB.sysAlert('client.tab_visit_project_permission_error');
            await TB.wait(500);

            return this.props.goToStep(BiddingBriefStep);
        }
        if (this.props.project.is_on_hold) {
            await TB.sysAlert('client.tab_visit_disabled_project_on_hold');
            return this.props.goToStep(BiddingBriefStep);
        }
        if (this.props.project.positions?.length) {
            let total = TB.sumBy(this.props.project.positions, pos => {
                if (!pos.total_selected_candidates && pos.assigned_candidate) {
                    return 1;
                }
                return pos.total_selected_candidates
            });
            if (total <= 0) {
                await TB.sysAlert('client.tab_visit_missing_candidate_proposals');
                return this.props.goToStep(BiddingTalentStep);
            }
        }
        callback();
    }

    proposedTalent() {
        return this.state.potentialTalent;
    }


    async onRequest(request) {

        await this.props.toggleUsability(false);

        let response = await request();

        await this.props.toggleUsability(true);

        return response;
    }

    render() {

        if (this.state.isLoading) {
            return (
                <div className="container-fluid flex-grow">
                    <div className="row">
                        <div className="col-12 flex flex-col">
                            {!this.props.location.pathname.includes('profiles') && (
                                <React.Fragment>
                                    {this.renderStepHeader()}
                                    <hr className="project-bidding-step-divider"/>
                                </React.Fragment>
                            )}

                            <LoadingBanner
                                message={this.props.location.pathname.includes('profiles') ?
                                    "Loading" :
                                    "Finding Matches"}/>
                        </div>
                    </div>
                </div>
            )
        }
        let {
            project,
            isUpdating,
            isDisabled,
            onRequest
        } = this.props;

        let positions = project.sortedPositions();

        return (
            <React.Fragment>
                <div className={TB.mapClass("flex flex-col items-stretch gap-6 flex-grow px-2 sm:px-6", {
                    "opacity-50 pointer-events-none": isUpdating
                })} style={{paddingTop: 40, paddingBottom: 40}}>

                    {this.context.auth?.canManageClients() && <ClientLeadManagementTools/>}

                    {positions.map((position, index) => {
                        const displayIndex = index + 1;
                        return (
                            <ProjectPositionProvider key={`ppr-${position.id}`} positionId={position.id}>
                                <PositionShortlistedCandidates
                                    positionIndex={displayIndex}
                                    position={position}
                                    isDisabled={isDisabled}
                                    onRequest={onRequest}
                                />

                                {displayIndex < positions.length && <hr className="mx-9"/>}

                            </ProjectPositionProvider>
                        );
                    })}

                    {isUpdating && (
                        <div className="absolute flex flex-col items-center justify-center w-full h-full">
                            <LoadingSpinner size="lg"/>
                        </div>
                    )}
                </div>
                <ShareModal
                    onRequest={onRequest}
                    positions={positions}
                    disabled={isDisabled}
                    projectId={project?.id}
                />

            </React.Fragment>
        )
    }
}

const CandidatesShortlistNav = ({isDisabled, ...props}) => {
    const {
        project,
        isLoading,
        requestSOW,
        cancelSOW,
        isProjectUpdating
    } = useProject();
    const location = useLocation();
    const {auth} = useAuth();
    const navigate = useNavigate();

    if (isLoading || location.pathname.includes('profiles')) {
        return null;
    }

    const totalPositions = project.positions?.length;
    const filledPositions = project.positions.filter(posi => posi.hasBeenFilled())
    const canAuthRequestSOW = auth.canRequestSOWForProject(project);

    return (
        <Affix offsetBottom={-1} style={{zIndex: 100}} id="bidding-footer">
            <div className="bidding-footer-nav bidding-footer-alt drop-shadow-xl">
                <div className="w-full flex justify-end max-w-[1225px]">

                    <div className="flex items-center gap-x-8 md:gap-x-16">

                        <div>
                            {project.hasRequestedSOW() ? (
                                <p className="text-bold">
                                    <i className="fas fa-check mr-1 text-success"/>
                                    {`SoW ${project.isAwaitingContract() ? 'requested' : 'ready'} for ${filledPositions.length} positions`}
                                </p>
                            ) : (
                                <p className="text-bold">{`${filledPositions.length} of ${totalPositions} positions filled`}</p>
                            )}
                        </div>
                        <div className="hidden flex-row items-center gap-2 md:flex">

                            {project.positions && project.positions.map((position, index) => {

                                let hasBeenFilled = position.hasBeenFilled();
                                let talent = {};

                                if (hasBeenFilled && position.assigned_candidate) {
                                    talent = position.assignedCandidate()
                                }

                                let assigned = (hasBeenFilled && talent?.image_path);
                                // let active = parseInt(positionId) === position.id;
                                let key = `pos${position.id}-talent${talent?.id || 'unassigned'}`;

                                let targetPath = position.currentBriefStep();
                                let canNavigate = !location.pathname.includes(targetPath);

                                return (
                                    <div key={key}
                                         className={TB.mapClass("position-swiper-slide", {
                                             assigned,
                                             unassigned: !assigned,
                                             //  active
                                         })}
                                    >
                                        <div
                                            id={key}
                                            className={TB.mapClass("role-img", {
                                                clickable: canNavigate
                                            })}
                                            onClick={(e) => {
                                                if (canNavigate) {
                                                    navigate({
                                                        pathname: `/client/project/bidding/${project?.id}${targetPath}`,
                                                        search: '?from=shortlist'
                                                    })
                                                }

                                            }}
                                        >
                                            {(hasBeenFilled && talent?.image_path) ? (
                                                <UserImageCircle
                                                    user={talent}
                                                    hideTooltip={true}
                                                    size={50} className="img-fluid img-grayscale"/>
                                            ) : (
                                                <span>
                                                    <i className="fa fa-user-circle"/>
                                                    <span className="position-index">
                                                        {index + 1}
                                                    </span>
                                                </span>
                                            )}
                                        </div>

                                        <InfoTooltip type="dark" placement="bottom" target={key}
                                                     tooltipProps={{container: "#bidding-footer"}}>
                                            <div className="position-tooltip">
                                                <p>{position.title}</p>
                                                <p>
                                                    {hasBeenFilled ? talent.displayName?.(auth) : "TBD"}
                                                </p>
                                            </div>
                                        </InfoTooltip>
                                    </div>
                                )
                            })}
                        </div>

                        <div>

                            {project.hasRequestedSOW() ? (
                                project.isPendingContract() ? (
                                    <button
                                        type="button"
                                        id="request-sow-btn"
                                        disabled={isProjectUpdating || isDisabled}
                                        onClick={() => {
                                            if (!canAuthRequestSOW) {
                                                return TB.showPermissionError();
                                            }
                                            return navigate('../contract');
                                        }}
                                        className={TB.mapClass("btn btn-primary btn-block w-full", {
                                            'disabled-edit-link': !project.hasFilledAllPositions(),
                                        })}
                                    >
                                        View SOW
                                        {!project.hasRequestedSOW() && !canAuthRequestSOW && (
                                            <i className="fa fa-lock ml-1"/>
                                        )}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        disabled={isProjectUpdating || isDisabled}
                                        onClick={() => {
                                            if (!canAuthRequestSOW) {
                                                return TB.showPermissionError();
                                            }
                                            return cancelSOW();
                                        }}
                                        className={TB.mapClass("btn btn-primary btn-block w-full", {
                                            'disabled-edit-link': !canAuthRequestSOW
                                        })}
                                    >
                                        Cancel SOW Request
                                        {!canAuthRequestSOW && (
                                            <i className="fa fa-lock ml-1"/>
                                        )}
                                    </button>
                                )
                            ) : (
                                <React.Fragment>
                                    <button
                                        type="button"
                                        id="request-sow-btn"
                                        disabled={isProjectUpdating || isDisabled}
                                        onClick={() => {
                                            if (!canAuthRequestSOW) {
                                                return TB.showPermissionError();
                                            }
                                            return requestSOW();
                                        }}
                                        className={TB.mapClass("btn btn-primary btn-block w-full", {
                                            'disabled-edit-link': !project.hasFilledAllPositions() || !!project.contract_requested_at || !canAuthRequestSOW,
                                        })}
                                    >
                                        Request SOW
                                        {!project.hasRequestedSOW() && !canAuthRequestSOW && (
                                            <i className="fa fa-lock ml-1"/>
                                        )}
                                    </button>

                                    {!project.hasFilledAllPositions() && (
                                        <InfoTooltip type="gray" placement="bottom" target="request-sow-btn">
                                            <p className="p-0 text-white">All positions must be filled to request
                                                SoW</p>
                                        </InfoTooltip>
                                    )}
                                </React.Fragment>
                            )}
                        </div>

                    </div>
                </div>
            </div>
        </Affix>
    )
}
const CandidatesWizardStep = (props) => {
    const {project, update, isProjectUpdating} = useProject();

    const location = useLocation();
    const navigate = useNavigate();
    const {
        toggleUsability,
        queryParams,
        isDisabled,
        goToStep
    } = useOutletContext();

    const candidateRequestsRunning = useIsMutating({
        mutationKey: "candidateRequest",
        fetching: true
    })
    const overrideRequestsRunning = useIsMutating({
        mutationKey: "updateContractTerms",
        fetching: true
    })
    const taxPreferenceRequestsRunning = useIsMutating({
        mutationKey: "updateTaxPreferenceMutation",
        fetching: true
    })

    const requestRunning = candidateRequestsRunning > 0 || overrideRequestsRunning > 0 || taxPreferenceRequestsRunning > 0
    if (!project) return null;

    let onRequest = async (callback = async () => true) => {
        await toggleUsability(false);
        let response = await callback();
        await toggleUsability(true);
        return response;
    }

    if (project.isService()) {
        return (
            <TeamProposalsWizardStep/>
        )
    }

    return (
        <React.Fragment>
            <BiddingCandidatesStep
                project={project}
                navigate={navigate}
                updateProject={update}
                location={location}
                onRequest={onRequest}
                toggleUsability={toggleUsability}
                queryParams={queryParams}
                isDisabled={isDisabled || requestRunning}
                goToStep={goToStep}
            />
            {!project.isActive() && (
                <CandidatesShortlistNav
                    onRequest={onRequest}
                    isUpdating={isProjectUpdating || requestRunning}
                    isDisabled={isDisabled || requestRunning}
                />
            )}

        </React.Fragment>
    )
}

export {
    CandidatesWizardStep
}